import type { AgentPage } from '../../types/negotiationAgents';
import type { FlowValidationProblem } from '../../types/negotiationAgents';
import { toAgentProblem } from '../../utils/negotiationAgents/apiResponse';
import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  AiModelInfo,
  CreateFlowInput,
  CreateNegociacaoSituacaoInput,
  NegociacaoSituacaoComEscritorios,
  UpdateSituacaoEscritoriosInput,
  Flow,
  SandboxMessage,
  SandboxReplyResult,
  SandboxStartResult,
  type SandboxStartInput,
  type SandboxReplyInput,
  type SandboxMessagesInput,
  type SandboxConversationInput,
  UpdateFlowInput,
  WhatsappTemplateDetails,
  type ZapsignTemplateInfo,
} from '../../types/flows';
import { type LinhaDeEnvio } from '../../types/flows';
import { flowsService } from './services';

export const fetchFlows = createAsyncThunk<
  Flow[],
  { type?: string; scenarioType?: string; status?: string; escritorioId?: string } | undefined,
  { rejectValue: string }
>('flows/fetchFlows', async (params, { rejectWithValue }) => {
  try {
    return await flowsService.listFlows(params);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao carregar flows');
  }
});

export const fetchFlow = createAsyncThunk<Flow, string, { rejectValue: string }>(
  'flows/fetchFlow',
  async (flowId, { rejectWithValue }) => {
    try {
      return await flowsService.getFlow(flowId);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Erro ao carregar flow');
    }
  },
);

export const createFlow = createAsyncThunk<Flow, CreateFlowInput, { rejectValue: string }>(
  'flows/createFlow',
  async (data, { rejectWithValue }) => {
    try {
      return await flowsService.createFlow(data);
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Erro ao criar flow');
    }
  },
);

export const updateFlow = createAsyncThunk<
  Flow,
  { flowId: string; data: UpdateFlowInput },
  { rejectValue: FlowValidationProblem }
>('flows/updateFlow', async ({ flowId, data }, { rejectWithValue }) => {
  try {
    return await flowsService.updateFlow(flowId, data);
  } catch (error) {
    return rejectWithValue(toAgentProblem(error));
  }
});

export const fetchLinhasDeEnvio = createAsyncThunk<LinhaDeEnvio[], void, { rejectValue: string }>(
  'flows/fetchLinhasDeEnvio',
  async (_, { rejectWithValue }) => {
    try {
      return await flowsService.listLinhasDeEnvio();
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Erro ao carregar números de envio',
      );
    }
  },
);

export const fetchWhatsappTemplateDetails = createAsyncThunk<
  WhatsappTemplateDetails,
  string,
  { rejectValue: string }
>('flows/fetchWhatsappTemplateDetails', async (templateId, { rejectWithValue }) => {
  try {
    return await flowsService.getWhatsappTemplateById(templateId);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao carregar template WhatsApp',
    );
  }
});

export const sandboxStart = createAsyncThunk<
  SandboxStartResult,
  SandboxStartInput,
  { rejectValue: string }
>('flows/sandboxStart', async ({ flowId, variables, escritorioId }, { rejectWithValue }) => {
  try {
    return await flowsService.sandboxStart(flowId, variables, escritorioId);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao iniciar sandbox');
  }
});

export const sandboxReply = createAsyncThunk<
  SandboxReplyResult,
  SandboxReplyInput,
  { rejectValue: string }
>(
  'flows/sandboxReply',
  async ({ flowId, conversaId, message, escritorioId }, { rejectWithValue }) => {
    try {
      return await flowsService.sandboxReply(flowId, conversaId, message, escritorioId);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Erro ao enviar mensagem sandbox',
      );
    }
  },
);

export const sandboxFetchMessages = createAsyncThunk<
  AgentPage<SandboxMessage>,
  SandboxMessagesInput,
  { rejectValue: string }
>(
  'flows/sandboxFetchMessages',
  async ({ flowId, conversaId, page, escritorioId }, { rejectWithValue }) => {
    try {
      return await flowsService.sandboxMessages(flowId, conversaId, page, escritorioId);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Erro ao carregar mensagens sandbox',
      );
    }
  },
);

export const sandboxCleanup = createAsyncThunk<
  void,
  SandboxConversationInput,
  { rejectValue: string }
>('flows/sandboxCleanup', async ({ flowId, conversaId, escritorioId }, { rejectWithValue }) => {
  try {
    await flowsService.sandboxCleanup(flowId, conversaId, escritorioId);
  } catch (error) {
    return rejectWithValue(toAgentProblem(error).message);
  }
});

export const fetchAiModels = createAsyncThunk<AiModelInfo[], void, { rejectValue: string }>(
  'flows/fetchAiModels',
  async (_, { rejectWithValue }) => {
    try {
      return await flowsService.listAiModels();
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Erro ao carregar modelos');
    }
  },
);

export const createNegociacaoSituacao = createAsyncThunk<
  NegociacaoSituacaoComEscritorios,
  CreateNegociacaoSituacaoInput,
  { rejectValue: string }
>('flows/createNegociacaoSituacao', async (payload, { rejectWithValue }) => {
  try {
    return await flowsService.createNegociacaoSituacao(payload);
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : 'Erro ao criar situação');
  }
});

export const updateSituacaoEscritorios = createAsyncThunk<
  NegociacaoSituacaoComEscritorios,
  UpdateSituacaoEscritoriosInput,
  { rejectValue: string }
>('flows/updateSituacaoEscritorios', async (payload, { rejectWithValue }) => {
  try {
    return await flowsService.updateSituacaoEscritorios(payload);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao atualizar escritórios da situação',
    );
  }
});

export const generateMultiAgentConfig = createAsyncThunk<
  Record<string, unknown>,
  { description: string; currentConfig?: Record<string, unknown> },
  { rejectValue: string }
>('flows/generateMultiAgentConfig', async ({ description, currentConfig }, { rejectWithValue }) => {
  try {
    return await flowsService.generateMultiAgentConfig(description, currentConfig);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao gerar configuração multi-agent',
    );
  }
});

export const fetchZapsignTemplateInputs = createAsyncThunk<
  ZapsignTemplateInfo,
  string,
  { rejectValue: string }
>('flows/fetchZapsignTemplateInputs', async (templateId, { rejectWithValue }) => {
  try {
    return await flowsService.getZapsignTemplateInputs(templateId);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Erro ao carregar template ZapSign',
    );
  }
});
